import React from 'react';
import useRole from '../../../hooks/use-role';
import { cn } from '../../../utils/cn';
import { useOrganizationsDictStore } from '../../organizations/state/use-organizations-dict-store';
import { formatChatTime } from '../utils';
import Attachments from './Attachments';

const URL_PATTERN = /((?:https?:\/\/|www\.)[^\s<]+)/gi;
const TRAILING_PUNCTUATION = /[.,!?;:'"»)\]]+$/;

const linkify = (text) =>
  text.split(URL_PATTERN).map((part, index) => {
    if (index % 2 === 0) return part;

    const url = part.replace(TRAILING_PUNCTUATION, '');
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;

    return (
      <React.Fragment key={index}>
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline break-all hover:text-blue-800">
          {url}
        </a>
        {part.slice(url.length)}
      </React.Fragment>
    );
  });

const MessageContent = ({ body, attachments, time, timeClassName }) => {
  return (
    <>
      {body ? <p className="whitespace-pre-wrap break-words">{linkify(body)}</p> : null}
      <Attachments attachments={attachments} />
      {time ? <p className={cn('mt-1 text-xs', timeClassName)}>{time}</p> : null}
    </>
  );
};

const MessageBubble = ({ message }) => {
  const { isMeridianOrg } = useRole();
  const { organizationsById } = useOrganizationsDictStore();
  const time = formatChatTime(message.created_at);

  if (message.sender_role === 'system') {
    return (
      <div className="w-full rounded-2xl bg-[#E8E8E8] px-4 py-3 text-center text-sm text-gray-800">
        <MessageContent body={message.body} attachments={message.attachments} time={time} timeClassName="text-gray-500" />
      </div>
    );
  }

  if (message.sender_role === 'note') {
    return (
      <div className="self-center w-full max-w-lg rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
        <p className="mb-1 text-xs font-semibold uppercase">Только для сотрудников</p>
        <MessageContent body={message.body} attachments={message.attachments} time={time} timeClassName="text-amber-700" />
      </div>
    );
  }

  const isOperator = message.sender_role === 'operator';
  const orgName = isMeridianOrg && message.author ? organizationsById[message.author.organization_id] : '';
  const authorLabel = isOperator
    ? (message.author ? [message.author.fio, orgName].filter(Boolean).join(' · ') || 'Поддержка' : 'Поддержка')
    : '';

  return (
    <div className={cn('flex max-w-[85%] flex-col', isOperator ? 'self-end items-end' : 'self-start items-start')}>
      {isOperator ? <p className="mb-1 px-1 text-xs text-gray-500">{authorLabel}</p> : null}
      <div className={cn('rounded-2xl px-4 py-3 text-sm text-gray-800', isOperator ? 'bg-[#F3F4F6]' : 'bg-[#FFF4EB]')}>
        <MessageContent body={message.body} attachments={message.attachments} time={time} timeClassName="text-gray-500" />
      </div>
    </div>
  );
};

export default MessageBubble;
